import React, { useState } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Button,
	Row,
	Form,
} from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import * as yup from "yup";

import formNavigationUtils from "../utils/formNavigationUtils";
import InputElement, {
	IntegerInputElement,
	ZipInputElement,
} from "../features/forms/components/InputElement";
import { SelectorElement } from "../features/forms/components/InputElement";
import FormattedMaskInput from "../components/formattedMaskInput/FormattedMaskInput";

import { addCardToAccount } from "../api/AutopayApi";
import { failAlert, successAlertCustom } from "../utils/alertUtils";

import {
	stateOptions,
	CountryCodes,
	digitzsCardTypes,
} from "../constants/Constants";

export default function CreditCardPaymentMethod({
	dmsCustomerId,
	accountID = null,
	toggle,
}) {
	// local state mirrors (for defaultValues)
	const [type, setType] = useState("");
	const [cardHolder, setCardHolder] = useState("");
	const [cardNumber, setCardNumber] = useState("");
	const [cardExpires, setCardExpires] = useState("");
	const [cardCode, setCardCode] = useState("");
	const [billingStreet, setBillingStreet] = useState("");
	const [billingUnit, setBillingUnit] = useState("");
	const [billingCity, setBillingCity] = useState("");
	const [billingState, setBillingState] = useState("");
	const [billingZip, setBillingZip] = useState("");
	const [billingCountry, setBillingCountry] = useState("");

	// 1) Yup schema: every field required + simple format checks
	const schema = yup.object().shape({
		cardHolder: yup.string().required("Name on card is required"),
		cardNumber: yup.string().required("Card number is required"),
		cardExpires: yup
			.string()
			.matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "MM/YY")
			.required("Expiration date is required"),
		cardCode: yup
			.string()
			.matches(/^\d{3,4}$/, "3 or 4 digits")
			.required("CVV is required"),
		billingStreet: yup.string().required("Street is required"),
		billingCity: yup.string().required("City is required"),
		billingState: yup.string().required("State is required"),
		billingZip: yup.string().required("Zip is required"),
		billingCountry: yup.string().required("Country is required"),
	});

	// 2) Hook up React Hook Form
	const methods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			type: "",
			cardHolder: cardHolder,
			cardNumber: cardNumber,
			cardExpires: cardExpires,
			cardCode: cardCode,
			billingStreet: billingStreet,
			billingUnit: billingUnit,
			billingCity: billingCity,
			billingState: billingState,
			billingZip: billingZip,
			billingCountry: billingCountry,
		},
	});
	const { handleSubmit, setValue } = methods;

	// 3) onSubmit builds your nested request body
	const onSubmit = async (data) => {
		const payload = {
			accountID,
			card: {
				type: data.type,
				cardNumber: data.cardNumber.replace(/[\/\s-]/g, ""),
				expiry: data.cardExpires.replace(/[\/\s-]/g, ""),
				dmsCustomerId,
				cardHolder: data.cardHolder,
				code: data.cardCode,
				label: data.type + "-" + data.cardNumber.slice(-4),
				address: {
					street1: data.billingStreet,
					street2: data.billingUnit,
					city: data.billingCity,
					state: data.billingState,
					zip: data.billingZip,
				},
			},
		};

		try {
			const res = await addCardToAccount(payload);
			successAlertCustom(res.data.message);
			toggle();
		} catch (err) {
			failAlert(err?.response?.data?.message || "Failed to save card");
		}
	};

	return (
		<div className="col-lg-12 mx-auto">
			<FormProvider {...methods}>
				<Form onKeyDown={formNavigationUtils} onSubmit={handleSubmit(onSubmit)}>
					<Card className="pt-2">
						<CardHeader>
							<CardTitle>
								<h3>Setup Credit Card</h3>
							</CardTitle>
						</CardHeader>

						{/* Card details */}
						<CardBody className="mx-1">
							<Row>
								<InputElement
									{...methods}
									colSize="col-lg-3"
									name="cardHolder"
									label="Name"
									value={cardHolder}
									onChange={(e) => {
										setCardHolder(e.target.value);
										setValue("cardHolder", e.target.value);
									}}
									labelClass="required"
								/>
								<SelectorElement
									{...methods}
									colSize="col-lg-4"
									name="type"
									label="Card Type"
									value={type}
									onChange={(e) => setType(e.target.value)}
									options={digitzsCardTypes}
								/>
								<FormattedMaskInput
									{...methods}
									colSize="col-lg-4"
									format="#### #### #### ####"
									name="cardNumber"
									label="Card Number"
									value={cardNumber}
									onChange={(val) => {
										setCardNumber(val);
										setValue("cardNumber", val);
									}}
									labelClass="required"
								/>

								<FormattedMaskInput
									{...methods}
									colSize="col-md-2"
									format="##/##"
									mask={["M", "M", "Y", "Y"]}
									placeholder="MM/YY"
									name="cardExpires"
									label="Exp."
									value={cardExpires}
									onChange={(val) => {
										setCardExpires(val);
										setValue("cardExpires", val);
									}}
									labelClass="required"
								/>
								<FormattedMaskInput
									{...methods}
									colSize="col-lg-2"
									format="###"
									name="cardCode"
									value={cardCode}
									onChange={(val) => {
										setCardCode(val);
										setValue("cardCode", val);
									}}
									label="CVV"
									placeholder="Security Code"
									labelClass="required"
								/>
							</Row>
						</CardBody>

						{/* Billing address */}
						<CardBody className="row mx-1 mt-3">
							<CardTitle tag="h3" className="col-12 mb-3">
								Billing Address
							</CardTitle>

							<InputElement
								{...methods}
								colSize="col-lg-3"
								name="billingStreet"
								label="Street"
								value={billingStreet}
								onChange={(e) => {
									setBillingStreet(e.target.value);
									setValue("billingStreet", e.target.value);
								}}
								labelClass="required"
							/>

							<InputElement
								{...methods}
								colSize="col-lg-2"
								name="billingUnit"
								label="Apt/Unit/Bld"
								value={billingUnit}
								onChange={(e) => {
									setBillingUnit(e.target.value);
									setValue("billingUnit", e.target.value);
								}}
								labelClass="required"
							/>

							<InputElement
								{...methods}
								colSize="col-lg-3"
								name="billingCity"
								label="City"
								value={billingCity}
								onChange={(e) => {
									setBillingCity(e.target.value);
									setValue("billingCity", e.target.value);
								}}
								labelClass="required"
							/>

							<SelectorElement
								{...methods}
								colSize="col-2"
								name="billingState"
								label="State"
								value={billingState}
								onChange={(e) => {
									setBillingState(e.target.value);
									setValue("billingState", e.target.value);
								}}
								options={stateOptions}
								labelClass="required"
							/>

							<ZipInputElement
								{...methods}
								colSize="col-lg-2"
								name="billingZip"
								label="Zip"
								value={billingZip}
								onChange={(val) => {
									setBillingZip(val);
									setValue("billingZip", val);
								}}
								labelClass="required"
							/>

							<SelectorElement
								{...methods}
								colSize="col-lg-3"
								name="billingCountry"
								label="Country"
								value={billingCountry}
								onChange={(e) => {
									setBillingCountry(e.target.value);
									setValue("billingCountry", e.target.value);
								}}
								options={CountryCodes}
								labelClass="required"
							/>
						</CardBody>

						{/* Submit */}
						<CardBody className="row mx-1 mt-3">
							<div className="col-12 d-flex justify-content-center my-3">
								<Button color="primary" type="submit">
									Save Payment Method
								</Button>
							</div>
						</CardBody>
					</Card>
				</Form>
			</FormProvider>
		</div>
	);
}
